
    import { Vue, Component } from 'vue-property-decorator'
    import api from '../utils/api'
    import PagedTrainingRecordsDTO from '../DTOs/PagedTrainingRecordsDTO'
    import { PagedTrainingRecordVM } from '../viewmodels/TrainingRecordVM'
    import Option from '../models/Option';
    import CurrentUser from '../utils/CurrentUser';
    import Location from '../models/Location';

    interface PagingData {
        page: number;
        itemsPerPage: number;
        sortBy: string[];
        sortDesc: string[];
        DogSearch: string;
        HandlerSearch: string;
        LocationSearch: string;
        TaskSearch: string;
        SubstanceSearch: string;
        StatusSearch: string;
        KnownBlindSearch: string;
        PositiveBlankSearch: string;
        VisualHiddenSearch: string;
        OnOffLeadSearch: string;
        IncludeArchived: boolean | null;
    }

    interface FilterOptions {
        Locations: Location[];
        Tasks: Option[];
        Substances: Option[];
        Statuses: string[];
    }

    @Component({
        name: 'TrainingRecords'
    })
    export default class TrainingRecords extends Vue {
        select: string = '';
        loadingData: boolean = false;
        showError: boolean = false;
        loadingRecords: boolean = false;
        filtersChanged: boolean = false;
        totalRecords: number = 0;
        pagingData: PagingData = { page: 1, itemsPerPage: 20, sortBy: [], sortDesc: [], DogSearch: '', HandlerSearch: '', LocationSearch: '', TaskSearch: '', SubstanceSearch: '', StatusSearch: '', KnownBlindSearch: '', PositiveBlankSearch: '', VisualHiddenSearch: '', OnOffLeadSearch: '', IncludeArchived: false };
        searchFields: any = { DogSearch: '', HandlerSearch: '', LocationSearch: '', TaskSearch: '', SubstanceSearch: '', StatusSearch: '', KnownBlindSearch: '', PositiveBlankSearch: '', VisualHiddenSearch: '', OnOffLeadSearch: '', showArchived: false };
        canAddRecords: boolean = false;
        fab: boolean = false;
        expanded: any[] = [];
        openedPanel: any = null;

        filterOptions: FilterOptions = { Locations: [], Tasks: [], Substances: [], Statuses: [] };

        headers = [
            { text: 'Date & Time', value: 'Completed', filterable: false, width: '20%' },
            { text: 'Team', value: 'Team', sortable: false, width: '15%' },
            { text: 'Locations', value: 'Locations', width: '15%' },
            { text: 'Type', value: 'Type', width: '10%' },
            { text: 'Tasks', value: 'Tasks', width: '10%' },
            { text: 'Substances', value: 'Substances', sortable: false, width: '15%' },
            { text: 'Status', value: 'Status', width: '10%' },
        ];

        trainingRecords: PagedTrainingRecordVM[] = [];

        async created() {
            this.canAddRecords = CurrentUser.hasPermission('EditTrainingRecords');
            this.loadingData = true;

            let trainingDropdownsResponse = await api.get<FilterOptions>('Training/PopulateDropdowns?userToken=' + CurrentUser.currentUserToken());

            if (trainingDropdownsResponse.ok) {
                this.filterOptions = trainingDropdownsResponse.data!;

                let filterRequired = false;
                let storedPagingData = localStorage.getItem("trainingRecordPagingData");
                let storedSearchFields = localStorage.getItem("trainingRecordSearchFields");

                if (storedPagingData) {
                    this.pagingData = JSON.parse(storedPagingData);
                    filterRequired = true;
                }

                if (storedSearchFields) {
                    this.searchFields = JSON.parse(storedSearchFields);
                    filterRequired = true;
                }
                if (filterRequired) {
                    this.openedPanel = 0;
                    this.searchRecords();
                }
            }
            else {
                this.showError = true;
            }
            this.loadingData = false;
        }

        async searchRecords() {
            this.savePagingData();

            this.loadRecords(this.pagingData);
            this.scrollToResults();
        }

        savePagingData() {
            localStorage.setItem("trainingRecordPagingData", JSON.stringify(this.pagingData));
            localStorage.setItem("trainingRecordSearchFields", JSON.stringify(this.searchFields));
        }

        async loadRecords(pagingDataEntry: PagingData){
            this.loadingRecords = true;
            this.filtersChanged = false;
            this.pagingData = pagingDataEntry; 

            this.pagingData.DogSearch = this.searchFields.DogSearch;
            this.pagingData.HandlerSearch = this.searchFields.HandlerSearch;
            this.pagingData.LocationSearch = this.searchFields.LocationSearch;
            this.pagingData.TaskSearch = this.searchFields.TaskSearch;
            this.pagingData.SubstanceSearch = this.searchFields.SubstanceSearch;
            this.pagingData.StatusSearch = this.searchFields.StatusSearch;
            this.pagingData.KnownBlindSearch = this.searchFields.KnownBlindSearch;
            this.pagingData.PositiveBlankSearch = this.searchFields.PositiveBlankSearch;
            this.pagingData.VisualHiddenSearch = this.searchFields.VisualHiddenSearch;
            this.pagingData.OnOffLeadSearch = this.searchFields.OnOffLeadSearch;
            this.pagingData.IncludeArchived = this.searchFields.showArchived ? this.searchFields.showArchived : false;

            let trainingRecordsResponse = await api.post<PagedTrainingRecordsDTO>('Training/PagedRecords?userToken=' + CurrentUser.currentUserToken(), this.pagingData);

            if (trainingRecordsResponse.ok) {
                this.trainingRecords = trainingRecordsResponse.data?.PagedTrainingRecords ?? [];
                this.totalRecords = trainingRecordsResponse.data?.Total ?? 0;
            }
            else {
                this.showError = true;
            }
            this.loadingRecords = false;
        }

        viewEditTrainingRecord(item: PagedTrainingRecordVM, index:any, event: any) {
            if (event.target.cellIndex < (this.headers.length - 1)){
                this.$router.push('/TrainingRecordDetails/' + item.ID);
            }
        }

        createNewTrainingRecord(){
            this.$router.push('/TrainingRecordDetails/0');
        }

        resetFilter() {
            Object.entries(this.searchFields).forEach(([key]) => {
              this.searchFields[key] = '';
            });

            localStorage.removeItem("trainingRecordPagingData");
            localStorage.removeItem("trainingRecordSearchFields");

            this.loadRecords(this.pagingData);
            this.scrollToResults();
        }

        getRowClass(item: PagedTrainingRecordVM) {
            return item.Status == 'Archived' ? 'archived-row' : '';
        }

        scrollToResults() {
            this.$nextTick(() => {
                const el = this.$el.querySelector(".v-data-table:first-of-type");
                const yOffset = -88;
                const y = el!.getBoundingClientRect().top + window.pageYOffset + yOffset;

                window.scrollTo({ top: y, behavior: 'smooth' });

                return;
            });
        }

        onScroll(e: any) {
            if (typeof window === 'undefined') return
            const top = window.pageYOffset || e.target.scrollTop || 0
            this.fab = top > 1500
        }

        toTop() {
            this.$vuetify.goTo(0);
        }

        expandClick(item: PagedTrainingRecordVM){
            const index = this.expanded.indexOf(item);
            if (index === -1){
                this.expanded.push(item);

            }
            else {
                this.expanded.splice(index, 1);
            }
        }
    }
